import { Box, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { memo } from "react";
import { TooltipLabel } from "components";
import PropTypes from "prop-types";

import theme from "themeProvider";

const FGDoCreatedCard = ({ doGroup }) => {
  
  const fgReadyTotal = (doGroup?.lineItems || []).reduce(
    (sum, item) => sum + (item.fgReadyQtyValue || 0),
    0,
  );

  const totalQty = (doGroup?.lineItems || []).reduce(
    (sum, item) =>
      sum + (item.fgReadyQtyValue || 0) + (item.expectedFgValue || 0),
    0,
  );

  const isRejected = doGroup?.transactionStatus === "Rejected";

  const statusBorderColor = isRejected
    ? theme.palette.grey[1000]
    : theme.palette.success.dark;

  const statusBgColor = isRejected
    ? theme.palette.grey.A900
    : theme.palette.success.main;

  const statusTextColor = isRejected
    ? theme.palette.text.secondary
    : theme.palette.success.dark;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: statusBorderColor,
        borderRadius: 1.5,
        margin: 2,
        padding: 2,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        backgroundColor: statusBgColor,
      }}
    >
      <Box
        sx={{
          padding: "12px 16px",
          minWidth: "90px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            borderRight: "1px solid",
            borderColor: statusTextColor,
            pr: 5,
            py: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "12px",
              color: statusTextColor,
            }}
          >
            DO STATUS
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          padding: "8px 6px",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
          DO ID
        </Typography>
        <Typography
          sx={{ fontWeight: 600, fontSize: "13px", color: "text.primary" }}
        >
          {doGroup?.doId}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: "8px 16px",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
          FG Ready
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              color: statusTextColor,
            }}
          >
            {fgReadyTotal}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            / {totalQty} MT — complete
          </Typography>
          <Box
            sx={{
              border: "1px solid",
              borderColor: statusTextColor,
              borderRadius: 10,
              padding: "1px 8px",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                color: statusTextColor,
                fontWeight: 600,
              }}
            >
              ✓ FG Ready
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          padding: "8px 16px",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
            DO Created on
          </Typography>
          <TooltipLabel
            placement="bottom"
            title={
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography fontSize={12}>
                  DO Created Timestamp: {doGroup?.doCreatedOn ?? "-"}
                </Typography>
                <Typography fontSize={12}>
                  DO Released Timestamp: {doGroup?.doReleasedOn ?? "-"}
                </Typography>
                {doGroup?.transactionStatus !== "Release" && (
                  <Typography fontSize={12}>
                    DO Cancelled Timestamp: {doGroup?.doCancelledOn ?? "-"}
                  </Typography>
                )}
              </Box>
            }
            labelChildren={
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                  cursor: "pointer",
                }}
              />
            }
          />
        </Box>

        <Typography
          sx={{ fontWeight: 600, fontSize: "13px", color: "text.primary" }}
        >
          {doGroup?.doCreatedOn}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: "8px 16px",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
          Transaction Status
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 10,
            padding: "2px 10px",
            display: "inline-block",
            mt: 0.5,
          }}
        >
          <Typography
            sx={{ fontSize: "12px", color: "primary.main", fontWeight: 600 }}
          >
            {`${
              doGroup?.transactionStatus
                ? "DO " + doGroup?.transactionStatus
                : doGroup?.transactionStatus
            }`}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          padding: "8px 16px",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
          DO Created By
        </Typography>
        <Typography
          sx={{ fontWeight: 600, fontSize: "13px", color: "text.primary" }}
        >
          {doGroup?.doCreatedBy}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: "8px 16px",
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
          DO Last Modified By
        </Typography>
        <Typography
          sx={{ fontWeight: 600, fontSize: "13px", color: "text.primary" }}
        >
          {doGroup?.doLastModifiedBy}
        </Typography>
      </Box>
    </Box>
  );
};
FGDoCreatedCard.propTypes = {
  doGroup: PropTypes.shape({
    doId: PropTypes.string,
    doCreatedOn: PropTypes.string,
    doReleasedOn: PropTypes.string,
    doCancelledOn: PropTypes.string,
    transactionStatus: PropTypes.string,
    doCreatedBy: PropTypes.string,
    doLastModifiedBy: PropTypes.string,
    lineItems: PropTypes.array,
  }),
};
export default memo(FGDoCreatedCard);